import React, { useState } from 'react';
import Delta from 'quill-delta';
import { Controller } from 'react-hook-form';
import makeStyles from '@mui/styles/makeStyles';
import { Box, Theme, Typography } from '@mui/material';
import { convertHtmlToDelta, checkIfQuillValueIsEmpty } from 'utils/quillUtils';
import ReactQuill, { ReactQuillProps } from 'react-quill';
import { FieldValues } from 'react-hook-form/dist/types/fields';
import { UseControllerProps } from 'react-hook-form/dist/types/controller';
import { RenderWithCondition } from '../../shared/Conditional/RenderWithCondition';
import { Control, UseFormGetValues, UseFormSetValue } from 'react-hook-form/dist/types/form';

interface Props<FormValues extends FieldValues = FieldValues> extends ReactQuillProps {
  label?: string;
  dataTestId?: string;
  control: Control<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  getValues: UseFormGetValues<FormValues>;
  name: UseControllerProps<FormValues>['name'];
}

export const QuillInputField = <FormValues extends FieldValues = FieldValues>({
  name,
  label,
  control,
  getValues,
  setValue,
  dataTestId,
  ...other
}: Props<FormValues>) => {
  const classes = useStyles({ style: other?.style ?? {} });

  const [innerValue, setInnerValue] = useState<ReactQuillProps['value']>(() => {
    const htmlValue = getValues(name);
    if (!checkIfQuillValueIsEmpty(htmlValue)) return convertHtmlToDelta(getValues(name));
    return new Delta() as any as ReactQuillProps['value'];
  });

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Box className={classes.root} data-test-id={dataTestId}>
          <RenderWithCondition condition={!!label}>
            <Typography component="span" variant="body1">
              {label}
            </Typography>
          </RenderWithCondition>

          <ReactQuill
            {...field}
            value={innerValue}
            onChange={(value, delta, source, editor) => {
              if (source === 'user') {
                setInnerValue(editor.getContents());
                setValue(field.name, editor.getHTML() as any);
              }
            }}
            {...other}
          />
        </Box>
      )}
    />
  );
};

const useStyles = makeStyles<Theme, { style?: React.CSSProperties }>(theme => ({
  root: ({ style }) => ({
    '& .ql-tooltip.ql-hidden': {
      display: 'none',
    },
    ...(style?.minHeight && { '& .ql-editor': { minHeight: style?.minHeight } }),
  }),
}));
